import React, { useEffect, useId, useRef, useState } from "react";
import { FiAlertTriangle, FiImage, FiRotateCcw, FiUpload } from "react-icons/fi";
import Compress from "browser-image-compression";

import { Cover, CoverFrame, CoverEmpty, CoverHead, CoverActions, CoverButton, CoverHint } from "./styles";

interface CoverPickerProps {
  label: string;
  /** Proporção do quadro: horizontal 16:9, vertical 2:3. */
  shape: "horizontal" | "vertical";
  required?: boolean;
  /** URL da capa já gravada (modo edição). */
  currentUrl?: string;
  /** Base64 cru da nova imagem escolhida ("" = nenhuma). */
  value: string;
  onChange: (base64: string) => void;
}

const ACCEPT = "image/png,image/jpeg,image/webp";

/**
 * Uma capa do curso: mostra a atual (ou a recém-escolhida) no quadro na
 * proporção real e troca por upload. Gera o mesmo base64 cru, comprimido a
 * ≤1 MB, que o `ImageUploadV2` gerava — o backend não muda.
 */
const CoverPicker: React.FC<CoverPickerProps> = ({
  label,
  shape,
  required = false,
  currentUrl,
  value,
  onChange,
}) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState("");
  const [broken, setBroken] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // O pai limpou o valor (Limpar/Descartar): some a prévia da nova imagem.
  useEffect(() => {
    if (!value) setPreview("");
  }, [value]);

  const existing = currentUrl?.trim() ? currentUrl.trim() : "";
  const shown = preview || existing;

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!ACCEPT.split(",").includes(file.type)) {
      setError("Use PNG, JPEG ou WEBP.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const compressed = await Compress(file, { maxSizeMB: 1, useWebWorker: true });
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(compressed);
      });
      setPreview(dataUrl);
      setBroken(false);
      onChange(dataUrl.split("base64,")[1] ?? "");
    } catch {
      setError("Não foi possível ler a imagem. Tente outro arquivo.");
    } finally {
      setBusy(false);
    }
  };

  const undo = () => {
    setPreview("");
    setError("");
    onChange("");
  };

  return (
    <Cover>
      <CoverHead>
        <label htmlFor={inputId}>
          {label}
          {required && <span aria-hidden> *</span>}
        </label>
        {preview && <CoverHint tone="gold">Nova</CoverHint>}
      </CoverHead>

      <CoverFrame shape={shape}>
        {shown && !(broken && !preview) ? (
          <img
            src={shown}
            alt={preview ? `${label} nova` : `${label} atual`}
            onError={() => setBroken(true)}
          />
        ) : (
          <CoverEmpty warn={broken && !preview}>
            {broken && !preview ? <FiAlertTriangle size={20} aria-hidden /> : <FiImage size={20} aria-hidden />}
            <span>{broken && !preview ? "Imagem indisponível" : "Sem capa"}</span>
          </CoverEmpty>
        )}
      </CoverFrame>

      <CoverActions>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={ACCEPT}
          onChange={handleFile}
          hidden
        />
        <CoverButton type="button" onClick={() => inputRef.current?.click()} disabled={busy}>
          <FiUpload size={14} aria-hidden />
          {busy ? "Processando…" : shown ? "Trocar" : "Escolher imagem"}
        </CoverButton>
        {preview && (
          <CoverButton type="button" quiet onClick={undo} aria-label={`Desfazer troca da ${label.toLowerCase()}`}>
            <FiRotateCcw size={14} aria-hidden />
            Desfazer
          </CoverButton>
        )}
      </CoverActions>

      {error && <CoverHint role="alert" tone="error">{error}</CoverHint>}
      {!error && broken && !preview && (
        <CoverHint tone="error">O arquivo desta capa não existe mais. Escolha outra.</CoverHint>
      )}
    </Cover>
  );
};

export default CoverPicker;
